'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  detectRegion,
  getConsent,
  saveConsent,
  logConsentAction,
  type ConsentRegion,
} from '@/lib/consent';
import ConsentPreferenceCenter from './ConsentPreferenceCenter';

export default function CookieBanner() {
  const [region, setRegion] = useState<ConsentRegion>('none');
  const [hasConsented, setHasConsented] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    const detectedRegion = detectRegion();
    setRegion(detectedRegion);

    // Check if user already consented
    const existing = getConsent();
    if (existing) {
      setHasConsented(true);
    } else if (detectedRegion !== 'none') {
      logConsentAction('banner_shown', detectedRegion);
    }
  }, []);

  const handleAcceptAll = () => {
    saveConsent(
      {
        necessary: true,
        analytics: true,
        marketing: true,
        preferences: true,
      },
      region
    );
    logConsentAction('accept_all', region);
    setHasConsented(true);
  };

  const handleRejectAll = () => {
    saveConsent(
      {
        necessary: true,
        analytics: false,
        marketing: false,
        preferences: false,
      },
      region
    );
    logConsentAction('reject_all', region);
    setHasConsented(true);
  };

  const handleManagePreferences = () => {
    logConsentAction('preferences_opened', region);
    setShowPreferences(true);
  };

  // GDPR Banner
  if (region === 'gdpr' && !hasConsented) {
    return (
      <>
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border shadow-lg">
          <div className="max-w-6xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-4">
              <div>
                {/* h2, not h3: the banner is appended to the document body
                    after the page's own <h1>, so an <h3> here skips a level
                    (h1 -> h3) and fails WCAG 1.3.1 / axe "heading-order" on
                    every page that renders it. */}
                <h2 className="text-sm font-semibold text-foreground mb-2">
                  Cookie & Privacy Consent
                </h2>
                <p className="text-xs text-muted-foreground mb-3">
                  We use cookies and similar technologies to enhance your experience, analyze site
                  usage, and deliver personalized content. By clicking "Accept All", you consent to
                  our use of cookies as described in our{' '}
                  <Link href="/privacy" className="text-primary underline">
                    Privacy Policy
                  </Link>{' '}
                  and{' '}
                  <Link href="/cookies" className="text-primary underline">
                    Cookie Policy
                  </Link>
                  .
                </p>
              </div>
              <div className="flex flex-wrap gap-3 justify-end">
                <button
                  onClick={handleRejectAll}
                  className="px-4 py-2 text-sm font-medium text-foreground bg-surface border border-border rounded-lg hover:bg-muted transition-colors"
                >
                  Reject All
                </button>
                <button
                  onClick={handleManagePreferences}
                  className="px-4 py-2 text-sm font-medium text-foreground bg-surface border border-border rounded-lg hover:bg-muted transition-colors"
                >
                  Manage Preferences
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
        {showPreferences && (
          <ConsentPreferenceCenter
            region={region}
            onClose={() => setShowPreferences(false)}
            onSave={() => {
              setShowPreferences(false);
              setHasConsented(true);
            }}
          />
        )}
      </>
    );
  }

  // CCPA Banner
  if (region === 'ccpa' && !hasConsented) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border shadow-lg">
        <div className="max-w-6xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground">
              We use cookies and similar technologies to enhance your experience. California
              residents can{' '}
              <Link href="/privacy#do-not-sell" className="text-primary underline">
                opt out of the sale of personal information
              </Link>
              . See our{' '}
              <Link href="/privacy" className="text-primary underline">
                Privacy Policy
              </Link>
              .
            </p>
            <button
              onClick={() => {
                saveConsent(
                  {
                    necessary: true,
                    analytics: true,
                    marketing: false,
                    preferences: true,
                  },
                  region
                );
                logConsentAction('accept_all', region);
                setHasConsented(true);
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors flex-shrink-0"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
